import {TextInput, ControlButton, TscButtonType, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import copy from 'copy-to-clipboard';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import Constants from '../../constants/Constants';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {themeStore} from '../../context/themeProvider';
import accessPhraseApi from '../../service/accessPhraseApi';
import logging from '../../service/logging';
import mediaApi from '../../service/mediaApi';
import StringUtil from '../../util/StringUtil';

export const InputWrapper = styled.div`
   margin-top: 0.125rem;
`;

const ButtonWrapper = styled.div`
   margin-top: 0.5rem;
`;

export const SetAccessPhraseBase: React.FC<ISetAccessPhraseProps & IStateMappedProps> = ({mediaHash, theme, setCurrentCopy, currentCopy}) => {
   const {t} = useTranslation();
   const [accessPhraseInputValue, setAccessPhraseInputValue] = useState('');
   const [errorMessage, setErrorMessage] = useState(null);
   const [isLoading, setIsLoading] = useState(false);
   const [currentAccessPhrase, setCurrentAccessPhrase] = useState('');
   const [copyButtonText, setCopyButtonText] = useState<string>(t('general.copy'));

   useEffect(() => {
      void initializeAccessPhrase();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      setCopyButtonText(!StringUtil.isNullOrWhiteSpace(currentCopy) && currentCopy === accessPhraseInputValue ? t('general.copied') : t('general.copy'));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentCopy]);

   const initializeAccessPhrase = async () => {
      try {
         const curAccessPhrase = await mediaApi.getMediaAccessPhrase(mediaHash);

         if (!StringUtil.isNullOrWhiteSpace(curAccessPhrase)) {
            setAccessPhraseInputValue(curAccessPhrase);
            setCurrentAccessPhrase(curAccessPhrase);
         } else {
            await generateAccessPhrase();
         }
      } catch {
         setErrorMessage(t('accessPhrase.failedToInitialize'));
      }
   };

   const onCopyClick = () => {
      copy(accessPhraseInputValue);
      setCopyButtonText(t('general.copied'));
      setCurrentCopy(accessPhraseInputValue);
   };

   const generateAccessPhrase = async () => {
      setIsLoading(true);
      setCopyButtonText(t('general.copy'));
      try {
         const generatedAccessPhrase = await accessPhraseApi.generateAccessPhrase();
         updateAccessPhrase(generatedAccessPhrase);
         await submitAccessPhrase(generatedAccessPhrase);
      } finally {
         setIsLoading(false);
      }
   };

   // If we assign a new access phrase on the same frame we submit, the forcedAccessPhrase parameter should be used
   const submitAccessPhrase = async (forcedAccessPhrase?: string) => {
      const accessPhraseToSubmit = sanitizeAccessPhrase(forcedAccessPhrase !== 'undefined' ? forcedAccessPhrase : accessPhraseInputValue);

      // if we fail sanitation we do not submit or if any errors come from the check
      if (accessPhraseToSubmit.length === 0 || StringUtil.validateAccessPhraseInput(accessPhraseToSubmit).length !== 0 || accessPhraseToSubmit === currentAccessPhrase) {
         return;
      }

      setIsLoading(true);
      setCopyButtonText(t('general.copy'));
      try {
         const returnedAccessPhrase = await mediaApi.updateMediaAccessPhrase(mediaHash, accessPhraseToSubmit);
         if (returnedAccessPhrase !== accessPhraseToSubmit) {
            setAccessPhraseInputValue(returnedAccessPhrase);
            logging.warn(`mismatched Access Phrase: The sent Access Phrase ${accessPhraseToSubmit} does not match the returned ${returnedAccessPhrase} due to differing normalization methods.`);
         }
         setCurrentAccessPhrase(returnedAccessPhrase);
      } catch {
         setErrorMessage(t('accessPhrase.failedToSubmit'));
      }
      setIsLoading(false);
   };

   const updateAccessPhrase = (newString: string): void => {
      setAccessPhraseInputValue(accessPhraseCheck(newString));
   };

   const onChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      updateAccessPhrase(evt.target.value);
   };

   const sanitizeAccessPhrase = (value: string) => {
      // all ws combination in input changed to a single space, note we also 'trim()' the value we actually submit
      // to server; so leading and trailing ws will not be submitted to handle accidental newlines and non-printables
      value = StringUtil.sanitizeAccessPhraseSubmit(value);
      if (value.length === 0) {
         setErrorMessage(t('accessPhrase.noLength'));
         return '';
      }
      setAccessPhraseInputValue(value);
      return value;
   };

   const accessPhraseCheck = (newString: string): string => {
      const errors = StringUtil.validateAccessPhraseInput(newString);
      setErrorMessage(null);
      if (errors.length !== 0) {
         if (errors.includes('accessPhrase.unprintableCharacter')) {
            setErrorMessage(t('accessPhrase.unprintableCharacter'));
            // eslint-disable-next-line no-control-regex
            newString = newString.replace(/[\x00-\x1F]+/g, '');
         }
         if (errors.includes('accessPhrase.maxLength')) {
            setErrorMessage(t('status.accessPhraseRequired.errorMaxLength', {maxLength: Constants.maxAccessPhraseLength}));
         }
         newString = newString.replace(/\s+/g, ' ');
      }
      return newString;
   };

   return (
      <div className="form-field-group">
         <InputWrapper className="button-group">
            <TextInput
               errorText={errorMessage}
               ariaLabel={t('accessPhrase.generateAccessPhrase')}
               onChange={onChange}
               placeholder={t('accessPhrase.placeholder')}
               value={accessPhraseInputValue}
               onBlur={() => submitAccessPhrase(accessPhraseInputValue)}
               testId="access-phrase-text"
               themeName={theme}
               disabled={isLoading}
            />
            <ControlButton
               buttonType={TscButtonType.primary}
               label={copyButtonText}
               themeName={theme}
               onClick={onCopyClick}
               testId="accessphrase-copy-button"
               width={'5rem'}
               disabled={isLoading || accessPhraseInputValue.length === 0 || !StringUtil.isNullOrWhiteSpace(errorMessage)}
            />
         </InputWrapper>
         <ButtonWrapper>
            <ControlButton
               label={t('accessPhrase.generateAccessPhrase')}
               buttonType={TscButtonType.secondary}
               themeName={theme}
               onClick={generateAccessPhrase}
               testId="accessphrase-generate-button"
               isFullWidth={true}
               disabled={isLoading}
            />
         </ButtonWrapper>
      </div>
   );
};

export interface ISetAccessPhraseProps {
   mediaHash: string;
   setCurrentCopy?(e: string): void;
   currentCopy: string;
}

export interface IStateMappedProps {
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {mediaHash, setCurrentCopy, currentCopy}: ISetAccessPhraseProps,
   {theme}: Partial<IStateMappedProps>
): IStateMappedProps & ISetAccessPhraseProps => ({theme, mediaHash, setCurrentCopy, currentCopy});

export default withMemoizedContexts(mapStatesToProps, themeStore)(SetAccessPhraseBase);
